// Likes: a heart on every human song that has started playing (on the River cards, the song card and
// the song page). A like is a 5-star rating, so it counts for the Hall of Fame; un-liking removes
// the rating. Rating views are shared here so the heart and the stars always agree.
import { useEffect, type CSSProperties } from "react";
import { create } from "zustand";
import { api, ApiError } from "../api";
import { currentRoute, navigate } from "../lib/router";
import { useSession } from "../session";
import type { RatingView } from "./Stars";

export { LIKE_STARS } from "@eb/shared";
import { LIKE_STARS } from "@eb/shared";

interface LikeState {
  views: Record<string, RatingView>;
  set: (loopId: string, v: RatingView) => void;
}

export const useRatings = create<LikeState>((set) => ({
  views: {},
  set: (loopId, v) => set((s) => ({ views: { ...s.views, [loopId]: v } })),
}));

const loading = new Set<string>();

/** Fetch a song's rating view once (per user) into the shared store. */
export function loadRating(loopId: string, force = false): void {
  if (!force && (loading.has(loopId) || useRatings.getState().views[loopId])) return;
  loading.add(loopId);
  api<RatingView>("GET", `/api/loops/${encodeURIComponent(loopId)}/rating`)
    .then((v) => useRatings.getState().set(loopId, v))
    .catch(() => undefined)
    .finally(() => loading.delete(loopId));
}

/** Forget cached views (the signed-in user changed). */
export function resetRatings(): void {
  useRatings.setState({ views: {} });
}
let lastUser: string | null | undefined;
useSession.subscribe((s) => {
  const id = s.user?.id ?? null;
  if (id !== lastUser) {
    lastUser = id;
    resetRatings();
  }
});

/** Like (5 stars) or un-like a song; returns an error message or null. */
export async function toggleLike(loopId: string): Promise<string | null> {
  const v = useRatings.getState().views[loopId];
  const liked = v?.mine === LIKE_STARS;
  try {
    const next = await api<RatingView>(
      liked ? "DELETE" : "PUT",
      `/api/loops/${encodeURIComponent(loopId)}/rating`,
      liked ? undefined : { stars: LIKE_STARS },
    );
    useRatings.getState().set(loopId, next);
    return null;
  } catch (e) {
    return e instanceof ApiError ? e.message : "Couldn't save your like.";
  }
}

export function HeartIcon({ filled, size = 20 }: { filled: boolean; size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path
        d="M12 20.6s-7.6-4.6-9.3-9.4C1.5 7.8 3.6 4.4 7.1 4.4c2 0 3.6 1.1 4.9 2.9 1.3-1.8 2.9-2.9 4.9-2.9 3.5 0 5.6 3.4 4.4 6.8-1.7 4.8-9.3 9.4-9.3 9.4z"
        fill={filled ? "var(--like, #ff3b6b)" : "none"}
        stroke={filled ? "var(--like, #ff3b6b)" : "currentColor"}
        strokeWidth="1.8"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/**
 * The heart button for one song. Signed out, it leads to sign-in; on your own song or one that
 * can't be rated it isn't shown.
 */
export function LikeButton({
  loopId,
  author,
  className = "",
  withLabel = false,
  style,
}: {
  loopId: string;
  author: string;
  className?: string;
  withLabel?: boolean;
  style?: CSSProperties;
}) {
  const user = useSession((s) => s.user);
  const v = useRatings((s) => s.views[loopId]);
  useEffect(() => {
    if (user) loadRating(loopId);
  }, [user, loopId]);
  if (user && v && !v.canRate && v.mine === null) return null;
  const liked = v?.mine === LIKE_STARS;
  const label = liked ? `Unlike the song by ${author}` : `Like the song by ${author}`;
  return (
    <button
      type="button"
      className={`like-btn${liked ? " liked" : ""}${withLabel ? " with-label" : ""} ${className}`}
      aria-pressed={liked}
      aria-label={label}
      title={label}
      disabled={!!user && !v}
      style={style}
      data-testid="like"
      onClick={(e) => {
        e.stopPropagation();
        if (!user || !user.nickname) {
          navigate(`/signin?next=${encodeURIComponent(currentRoute())}`);
          return;
        }
        void toggleLike(loopId).then((err) => {
          if (err) window.alert(err);
          else void import("../river/EncoreMeter").then((m) => m.refreshNowLikes());
        });
      }}
    >
      <HeartIcon filled={liked} size={withLabel ? 20 : 18} />
      {withLabel && <span>{liked ? "Liked" : "Like"}</span>}
    </button>
  );
}
